import { useState } from "react";
import { Loader2, Play, MapPin, Filter } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface RunScanRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: string;
  requestName?: string | null;
  polygon: unknown;
  requestedVariables: Record<string, unknown> | null;
}

function getVertexCount(polygon: unknown): number {
  if (!polygon || typeof polygon !== "object") return 0;
  const p = polygon as { coordinates?: number[][][] };
  const ring = p.coordinates?.[0];
  if (!Array.isArray(ring)) return 0;
  return ring.length > 0 ? ring.length - 1 : 0;
}

export function RunScanRequestDialog({
  open,
  onOpenChange,
  requestId,
  requestName,
  polygon,
  requestedVariables,
}: RunScanRequestDialogProps) {
  const [configId, setConfigId] = useState<string>("");
  const [collectionMode, setCollectionMode] = useState<"create" | "existing">("create");
  const [newCollectionName, setNewCollectionName] = useState(requestName ?? "");
  const [existingCollectionId, setExistingCollectionId] = useState<string>("");

  const utils = trpc.useUtils();

  const { data: configsData } = trpc.scanConfigs.list.useQuery(undefined, {
    enabled: open,
  });
  const { data: collectionsData } = trpc.collections.list.useQuery(undefined, {
    enabled: open,
  });

  const runMutation = trpc.scanRequests.runRequest.useMutation({
    onSuccess: (data) => {
      toast.success(`Scan started (${data.scanId.slice(0, 8)}...)`);
      utils.scanRequests.list.invalidate();
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const configs = configsData ?? [];
  const collections = collectionsData ?? [];

  const sportTypes = (requestedVariables?.sportTypes as string[]) ?? [];
  const excludeInstitutions = requestedVariables?.excludeInstitutions as boolean | undefined;
  const excludePrivate = requestedVariables?.excludePrivate as boolean | undefined;
  const excludeNoSportField = requestedVariables?.excludeNoSportField as boolean | undefined;
  const vertexCount = getVertexCount(polygon);

  const canRun =
    configId &&
    (collectionMode === "create"
      ? newCollectionName.trim().length > 0
      : existingCollectionId.length > 0);

  const handleRun = () => {
    if (!canRun) return;

    runMutation.mutate({
      id: requestId,
      configId,
      collection:
        collectionMode === "create"
          ? { mode: "create", name: newCollectionName.trim() }
          : { mode: "existing", collectionId: existingCollectionId },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Run Scan Request</DialogTitle>
          <DialogDescription>
            Choose a scan config and collection to launch this scan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Request Summary */}
          <div className="rounded-md border p-3 space-y-1.5 text-sm bg-muted/50">
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              <span>{vertexCount} polygon vertices</span>
            </div>
            {sportTypes.length > 0 && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Filter className="h-3.5 w-3.5" />
                <span>{sportTypes.length} sport type{sportTypes.length !== 1 ? "s" : ""}</span>
              </div>
            )}
            {(excludeInstitutions !== undefined || excludePrivate !== undefined || excludeNoSportField !== undefined) && (
              <div className="text-xs text-muted-foreground">
                {(() => {
                  const includes = [
                    !excludeInstitutions && "institutional",
                    !excludePrivate && "private",
                    !excludeNoSportField && "without sport fields",
                  ].filter(Boolean);
                  const excludes = [
                    excludeInstitutions && "institutional",
                    excludePrivate && "private",
                    excludeNoSportField && "without sport fields",
                  ].filter(Boolean);
                  return (
                    <>
                      {includes.length > 0 && <>Includes: {includes.join(", ")}</>}
                      {includes.length > 0 && excludes.length > 0 && " · "}
                      {excludes.length > 0 && <>Excludes: {excludes.join(", ")}</>}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Config Selector */}
          <div className="space-y-2">
            <Label>Scan Config</Label>
            <Select value={configId} onValueChange={setConfigId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a config..." />
              </SelectTrigger>
              <SelectContent>
                {configs.map((cfg: any) => (
                  <SelectItem key={cfg.id} value={cfg.id}>
                    {cfg.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {configs.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No scan configs found. Create one first.
              </p>
            )}
          </div>

          {/* Collection Mode */}
          <div className="space-y-3">
            <Label>Collection</Label>
            <RadioGroup
              value={collectionMode}
              onValueChange={(v) => setCollectionMode(v as "create" | "existing")}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="create" id="col-create" />
                <Label htmlFor="col-create" className="font-normal cursor-pointer">
                  Create new collection
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="existing" id="col-existing" />
                <Label htmlFor="col-existing" className="font-normal cursor-pointer">
                  Add to existing collection
                </Label>
              </div>
            </RadioGroup>

            {collectionMode === "create" ? (
              <Input
                placeholder="Collection name"
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
              />
            ) : (
              <Select value={existingCollectionId} onValueChange={setExistingCollectionId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a collection..." />
                </SelectTrigger>
                <SelectContent>
                  {collections.map((col: any) => (
                    <SelectItem key={col.id} value={col.id}>
                      {col.name} ({col.locationCount ?? 0} locations)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleRun} disabled={!canRun || runMutation.isPending}>
            {runMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Run Scan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
